import { useState } from 'react';
import { Link } from 'react-router-dom';
import { verifyPasscode, verifyContractorLogin, verifyEmployeeLogin } from '../api/loginApi';
import { useAuthStore } from '../store/authStore';
import { LanguageSwitcher } from './LanguageSwitcher';
import { useTranslation } from '../hooks/useTranslation';
import { useLanguageStore } from '../store/languageStore';
import { getLogoPath } from '../utils/logoUtils';
import {
  digitPasswordMaskClass,
  isValidDigitPassword,
  sanitizeDigitPasswordInput,
} from '../utils/userPasswordInput';

type LoginMode = 'admin' | 'contractor' | 'employee';
type LoginStage = 'welcome' | 'login';

function renderTermsAgreement(
  agreementText: string,
  termsText: string,
  privacyText: string
): JSX.Element {
  // Split the text by terms and privacy text to insert links
  const parts = agreementText.split(termsText);
  if (parts.length !== 2) {
    // Fallback if split doesn't work as expected
    return <>{agreementText}</>;
  }
  
  const [beforeTerms, afterTerms] = parts;
  const middleParts = afterTerms.split(privacyText);
  
  if (middleParts.length !== 2) {
    // Fallback if split doesn't work as expected
    return <>{agreementText}</>;
  }
  
  const [betweenText, afterPrivacy] = middleParts;
  
  return (
    <>
      {beforeTerms}
      <Link 
        to="/documents/terms" 
        className="text-blue-600 hover:text-blue-800 underline mx-0.5"
        target="_blank"
        rel="noopener noreferrer"
      >
        {termsText}
      </Link>
      {betweenText}
      <Link 
        to="/documents/privacy" 
        className="text-blue-600 hover:text-blue-800 underline mx-0.5"
        target="_blank"
        rel="noopener noreferrer"
      >
        {privacyText}
      </Link>
      {afterPrivacy}
    </>
  );
}

export function LoginPage() {
  const { t } = useTranslation();
  const language = useLanguageStore((state) => state.language);
  const [stage, setStage] = useState<LoginStage>('welcome');
  const [mode, setMode] = useState<LoginMode | null>(null);
  const [passcode, setPasscode] = useState('');
  const [vatNumber, setVatNumber] = useState('');
  const [contractorPassword, setContractorPassword] = useState('');
  const [passportNumber, setPassportNumber] = useState('');
  const [employeePassword, setEmployeePassword] = useState('');
  const [requiresPassword, setRequiresPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { setJwt, setAuthUser } = useAuthStore();

  const resetForm = () => {
    setPasscode('');
    setVatNumber('');
    setContractorPassword('');
    setPassportNumber('');
    setEmployeePassword('');
    setRequiresPassword(false);
    setError('');
  };

  const handleUserTypeSelection = (selectedMode: LoginMode) => {
    setMode(selectedMode);
    resetForm();
    setStage('login');
  };

  const handleBackToWelcome = () => {
    setStage('welcome');
    setMode(null);
    resetForm();
  };

  const handleAdminLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await verifyPasscode(passcode);
    
    if (result.success && result.data?.jwt) {
      setJwt(result.data.jwt);
      // Set authUser for admin. Entry point selection is handled in admin flow.
      setAuthUser({
        userType: 'admin',
      });
    } else {
      setError(result.error || t('login.error.wrongPasscode'));
    }
    
    setLoading(false);
  };

  const handleContractorLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await verifyContractorLogin(vatNumber, contractorPassword || undefined);
    
    if (result.success && result.data?.jwt) {
      setJwt(result.data.jwt);
      setAuthUser({
        userType: 'contractor',
        contractorId: result.data.contractorId,
        contractorName: result.data.contractorName,
      });
    } else {
      // If error indicates password required, show password field
      if (result.error === 'נדרשת סיסמה' && !requiresPassword) {
        setRequiresPassword(true);
        setError(t('login.passwordRequired'));
      } else {
        setError(result.error || t('login.error.vatNotFound'));
      }
    }
    
    setLoading(false);
  };

  const handleEmployeeLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await verifyEmployeeLogin(passportNumber, employeePassword || undefined);
    
    if (result.success && result.data?.jwt) {
      setJwt(result.data.jwt);
      setAuthUser({
        userType: 'employee',
        employeeId: result.data.employeeId,
        employeeName: result.data.employeeName,
      });
    } else {
      if (result.error === 'נדרשת סיסמה' && !requiresPassword) {
        setRequiresPassword(true);
        setError(t('login.passwordRequired'));
      } else {
        setError(result.error || t('login.error.passportNotFound'));
      }
    }
    
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 overflow-hidden flex items-center justify-center bg-gradient-to-br from-blue-50 to-blue-100 p-0 touch-none overscroll-none">
      <div className="card max-w-md w-full mx-3 my-0 md:mx-4 md:my-4 p-5 md:p-6 overflow-hidden">
        {/* Welcome Stage */}
        {stage === 'welcome' && (
          <>
            <div className="text-center mb-6 md:mb-8">
              <div className="mb-10 md:mb-12">
                <img 
                  src={getLogoPath(language)} 
                  alt="לוריא דנאור" 
                  className="h-16 md:h-20 mx-auto"
                />
              </div>
              <h1 className="text-2xl md:text-4xl font-bold text-gray-800 mb-2 md:mb-3">
                {t('login.welcome')}
              </h1>
              <p className="text-sm md:text-base text-gray-500">
                {t('login.selectUserType')}
              </p>
            </div>

            <div className="space-y-5 md:space-y-6 mb-6">
              <button
                type="button"
                onClick={() => handleUserTypeSelection('contractor')}
                className="btn-primary w-full text-xl md:text-2xl py-6 md:py-7 font-bold shadow-xl hover:shadow-2xl hover:scale-[1.02] transform transition-all duration-200 flex items-center justify-center gap-3 rounded-xl"
              >
                <svg className="w-6 h-6 md:w-7 md:h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
                {t('login.iAmContractor')}
              </button>
              
              <button
                type="button"
                onClick={() => handleUserTypeSelection('employee')}
                className="btn-primary w-full text-xl md:text-2xl py-6 md:py-7 font-bold shadow-xl hover:shadow-2xl hover:scale-[1.02] transform transition-all duration-200 flex items-center justify-center gap-3 rounded-xl"
              >
                <svg className="w-6 h-6 md:w-7 md:h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                {t('login.iAmEmployee')}
              </button>
            </div>

            <div className="text-center">
              <button
                type="button"
                onClick={() => handleUserTypeSelection('admin')}
                className="text-sm md:text-base text-gray-500 hover:text-gray-700 underline"
              >
                {t('login.adminLogin')}
              </button>
            </div>

            <div className="mt-6 md:mt-8 text-center text-xs md:text-sm text-gray-600">
              <p>{t('login.needHelp')}</p>
            </div>
            
            {/* Language Switcher - bottom left */}
            <LanguageSwitcher position="bottom-left" />
          </>
        )}

        {/* Login Form Stage */}
        {stage === 'login' && (
          <>
            <div className="text-center mb-4 md:mb-6">
              <div className="mb-6 md:mb-8">
                <img 
                  src={getLogoPath(language)} 
                  alt="לוריא דנאור" 
                  className="h-12 md:h-16 mx-auto"
                />
              </div>
              <h1 className="text-xl md:text-3xl font-bold text-gray-800 mb-1 md:mb-2">
                {mode === 'contractor' && t('login.contractorLogin')}
                {mode === 'employee' && t('login.employeeLogin')}
                {mode === 'admin' && t('login.adminLoginTitle')}
              </h1>
              <button
                type="button"
                onClick={handleBackToWelcome}
                className="text-sm text-gray-500 hover:text-gray-700 mt-2"
              >
                {t('login.back')}
              </button>
            </div>

            {/* Contractor Login Form */}
            {mode === 'contractor' && (
              <form onSubmit={handleContractorLogin}>
                <div className="mb-4 md:mb-6">
                  <label htmlFor="vatNumber" className="block text-sm font-medium text-gray-700 mb-2">
                    {t('login.vatNumber')}
                  </label>
                  <input
                    id="vatNumber"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={vatNumber}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '');
                      if (value.length <= 9) {
                        setVatNumber(value);
                      }
                    }}
                    className="input-field text-center text-lg md:text-xl tracking-wider py-3"
                    placeholder={t('login.vatNumberPlaceholder')}
                    maxLength={9}
                    autoFocus
                    disabled={loading}
                  />
                  <p className="text-xs text-gray-500 mt-1 text-center">
                    {t('login.vatNumberHint')}
                  </p>
                </div>

                {requiresPassword && (
                  <div className="mb-4 md:mb-6">
                    <label htmlFor="contractorPassword" className="block text-sm font-medium text-gray-700 mb-2">
                      {t('login.password')}
                    </label>
                    <input
                      id="contractorPassword"
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      autoComplete="off"
                      value={contractorPassword}
                      onChange={(e) => setContractorPassword(sanitizeDigitPasswordInput(e.target.value))}
                      className={`input-field text-center text-lg md:text-xl tracking-wider py-3 ${digitPasswordMaskClass}`}
                      placeholder={t('login.passwordPlaceholder')}
                      maxLength={20}
                      autoFocus
                      disabled={loading}
                    />
                    <p className="text-xs text-gray-500 mt-1 text-center">
                      {t('login.passwordHint')}
                    </p>
                  </div>
                )}

                {error && (
                  <div className="mb-3 md:mb-4 p-2 md:p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg text-center text-sm md:text-base">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={
                    loading ||
                    vatNumber.length !== 9 ||
                    (requiresPassword && !isValidDigitPassword(contractorPassword))
                  }
                  className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed text-sm md:text-base py-3 flex items-center justify-center gap-2"
                >
                  {loading && (
                    <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  )}
                  {loading ? t('login.connecting') : t('login.submit')}
                </button>
              </form>
            )}

            {/* Employee Login Form */}
            {mode === 'employee' && (
              <form onSubmit={handleEmployeeLogin}>
                <div className="mb-4 md:mb-6">
                  <label htmlFor="passportNumber" className="block text-sm font-medium text-gray-700 mb-2">
                    {t('login.passportNumber')}
                  </label>
                  <input
                    id="passportNumber"
                    type="text"
                    value={passportNumber}
                    onChange={(e) => {
                      const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
                      if (value.length <= 9) {
                        setPassportNumber(value);
                      }
                    }}
                    className="input-field text-center text-lg md:text-xl tracking-wider py-3"
                    placeholder={t('login.passportNumberPlaceholder')}
                    maxLength={9}
                    autoFocus
                    disabled={loading}
                  />
                  <p className="text-xs text-gray-500 mt-1 text-center">
                    {t('login.passportNumberHint')}
                  </p>
                </div>

                {requiresPassword && (
                  <div className="mb-4 md:mb-6">
                    <label htmlFor="employeePassword" className="block text-sm font-medium text-gray-700 mb-2">
                      {t('login.password')}
                    </label>
                    <input
                      id="employeePassword"
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      autoComplete="off"
                      value={employeePassword}
                      onChange={(e) => setEmployeePassword(sanitizeDigitPasswordInput(e.target.value))}
                      className={`input-field text-center text-lg md:text-xl tracking-wider py-3 ${digitPasswordMaskClass}`}
                      placeholder={t('login.passwordPlaceholder')}
                      maxLength={20}
                      autoFocus
                      disabled={loading}
                    />
                    <p className="text-xs text-gray-500 mt-1 text-center">
                      {t('login.passwordHint')}
                    </p>
                  </div>
                )}

                {error && (
                  <div className="mb-3 md:mb-4 p-2 md:p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg text-center text-sm md:text-base">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={
                    loading ||
                    passportNumber.length < 5 ||
                    (requiresPassword && !isValidDigitPassword(employeePassword))
                  }
                  className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed text-sm md:text-base py-3 flex items-center justify-center gap-2"
                >
                  {loading && (
                    <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  )}
                  {loading ? t('login.connecting') : t('login.submit')}
                </button>
              </form>
            )}

            {/* Admin Login Form */}
            {mode === 'admin' && (
              <form onSubmit={handleAdminLogin}>
                <div className="mb-4 md:mb-6">
                  <label htmlFor="passcode" className="block text-sm font-medium text-gray-700 mb-2">
                    {t('login.passcode')}
                  </label>
                  <input
                    id="passcode"
                    type="password"
                    value={passcode}
                    onChange={(e) => setPasscode(e.target.value)}
                    className="input-field text-center text-xl md:text-2xl tracking-widest py-3"
                    placeholder={t('login.passcodePlaceholder')}
                    maxLength={4}
                    autoFocus
                    disabled={loading}
                  />
                </div>

                {error && (
                  <div className="mb-3 md:mb-4 p-2 md:p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg text-center text-sm md:text-base">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading || passcode.length === 0}
                  className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed text-sm md:text-base py-3 flex items-center justify-center gap-2"
                >
                  {loading && (
                    <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  )}
                  {loading ? t('login.connecting') : t('login.submit')}
                </button>
              </form>
            )}

            <div className="mt-4 md:mt-6 text-center text-xs md:text-sm text-gray-500">
              <p>
                {renderTermsAgreement(
                  t('login.termsAgreement'),
                  t('login.termsOfUse'),
                  t('login.privacyPolicy')
                )}
              </p>
            </div>

            <div className="mt-4 md:mt-6 text-center text-xs md:text-sm text-gray-600">
              <p>{t('login.needHelp')}</p>
            </div>
            
            {/* Language Switcher - bottom left (only for employee login) */}
            {mode === 'employee' && <LanguageSwitcher position="bottom-left" />}
          </>
        )}
      </div>
    </div>
  );
}

